/**
 * Fatture sul telefono (7/10/2026): risponde a "chi ci deve pagare, e chi è in ritardo?".
 *
 * Prima, a 390px: una frase di tre righe, tre riquadri di numeri, le linguette
 * col tasto dei filtri, poi schede alte mezzo schermo (RATA / QUANDO SCADE /
 * IMPORTO e tre bottoni): si vedeva una fattura e mezza per volta.
 * Qui:
 *   1. in grande, quanto ci devono i clienti; sotto, quante fatture sono in
 *      ritardo e da quanto la più vecchia;
 *   2. le viste: da incassare, emesse da noi, preventivi;
 *   3. da incassare: prima quelle col bonifico già in banca (basta
 *      confermare), poi in ritardo, poi quelle che devono ancora scadere. Una
 *      riga per rata: chi, che fattura, da quanto, quanto. Si tocca e sale il
 *      pannello con i gesti di prima: segna incassata, conferma il bonifico,
 *      prepara il sollecito, apri la fattura, togli dall'elenco;
 *   4. emesse da noi: per mese, con lo stato del pagamento nella riga;
 *      preventivi: per stato della firma. La riga apre il documento.
 * In fondo a "Da incassare" le fatture già incassate e quelle tolte
 * dall'elenco (`?status=`, come sul computer). Creare fatture e preventivi è
 * una procedura lunga: resta al computer, e lo dice una riga.
 * Stesse chiamate, stessa cache e stessi parametri nell'indirizzo del computer
 * (vista, status, q, type, ordina, storico): un link funziona su tutti e due.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { InvoiceStatus, ModuleKey, PermissionLevel, QuoteStatus, IGNORE_REASON_LABEL, hasAtLeast } from '@masamune/shared';
import { useAuthStore } from '../../../stores/auth.store';
import { useModalStore } from '../../../stores/modal.store';
import { Icon } from '../../../components/ui/Icon';
import type { ActionItem } from '../../../components/ui/ActionsMenu';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../../components/ui/OrdinaFiltra';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MSearch, MSeg, iniziali, leggibile } from '../../../components/mobile/MobileKit';
import { useUrlState } from '../../../hooks/useUrlState';
import { fmtEur } from '../../../lib/utils';
import { FATTURE_VISTE, type FattureVista } from '../../../lib/adminRoutes';
import { documentsQuery, type DocSource, type UnifiedRow } from '../../../lib/scadenziario';
import { STATUS_INFO } from '../../invoices/DocumentsSection';
import { canReadAdmin, canWriteAdmin, daysFromToday, incassiQuery, isoDay, plurale, METHOD_LABEL, type IncassoRow } from '../adminApi';
import { useAdminActions } from '../useAdminActions';
import { SettlePopover } from '../SettlePopover';
import { TrashPopover } from '../TrashPopover';
import { AdminHeader, useStorico, useVista } from '../shared/AdminShell';
import { Fatto, Foglio, Gesto, StoricoAcceso, Testo, aGruppi, euro, euroEsatto, euroRiga, giornoBreve, meseGruppo, nomeRiga } from '../shared/AdminMobile';
import { SollecitaPopover } from './SollecitaPopover';
import {
  daIncassareQuery, durata, etichettaRata, giorniDaOggi, quando, rataInParole,
  type DaIncassare, type RataDaIncassare,
} from './fattureApi';

/** Quante righe chiede una lista paginata, e quante ne aggiunge "Vedi altre". */
const PAGINA = 25;

const NOTA_CONFERMA = 'Confermata da Fatture da incassare: in banca c\'è il bonifico corrispondente.';

/** Il bonifico che non torna del tutto (campo dell'API, come in FattureDaIncassare). */
interface DubbioRata { txId: string; settledOn: string | null; amount: number; counterparty: string | null; label: string; testo: string; scegli: boolean }
const dubbioDi = (r: RataDaIncassare): DubbioRata | null => (r as RataDaIncassare & { dubbio?: DubbioRata | null }).dubbio ?? null;

type Az = { kind: 'incassa' | 'sollecita' | 'togli'; r: RataDaIncassare };

function livello(user: { permissions: Array<{ module: string; level: string }> } | null | undefined, mod: string): PermissionLevel {
  return (user?.permissions.find((p) => p.module === mod)?.level ?? PermissionLevel.NONE) as PermissionLevel;
}

export function FattureMobile() {
  const user = useAuthStore((s) => s.user);
  const openModal = useModalStore((s) => s.open);
  const canAdmin = canReadAdmin(user);
  const canWrite = canWriteAdmin(user);
  const canReadDocs = hasAtLeast(livello(user, ModuleKey.FATTURAZIONE), PermissionLevel.READ);
  const permesse = FATTURE_VISTE.filter((k) => (k === 'incassare' ? canAdmin : canReadDocs));
  const [vista, setVista] = useVista<FattureVista>(permesse.length ? permesse : FATTURE_VISTE);
  const storico = useStorico();
  const { data } = useQuery({ ...daIncassareQuery(storico.on), enabled: canAdmin });

  // I comandi rari stanno nel menu ⋯ della barra in alto, come prima.
  const contanti: ActionItem = {
    key: 'contanti', label: 'Registra un incasso in contanti', icon: 'banknote',
    onClick: () => openModal('newCashEntry', { direction: 'IN' }),
    hidden: !canWrite,
  };

  if (!permesse.length) {
    return (
      <div className="page adm-page">
        <AdminHeader title="Fatture" />
        <MPage><MEmpty>Le fatture non sono per il tuo profilo: per vederle serve l'accesso all'Amministrazione o alla Fatturazione.</MEmpty></MPage>
      </div>
    );
  }

  const aperte = data?.items.length;
  const viste = permesse.length > 1 ? (
    <MSeg<FattureVista>
      value={vista}
      onChange={setVista}
      options={permesse.map((k) => ({
        key: k,
        label: k === 'incassare' ? 'Da incassare' : k === 'documenti' ? 'Emesse da noi' : 'Preventivi',
        count: k === 'incassare' && aperte ? aperte : undefined,
      }))}
    />
  ) : null;

  return (
    <div className="page adm-page">
      <AdminHeader title="Fatture" menu={[storico.menuItem, contanti]} />
      <MPage>
        {canAdmin && data && <Risposta d={data} />}
        {storico.on && <StoricoAcceso year={storico.year} onOff={() => storico.set(false)} />}
        {vista === 'incassare' && canAdmin && <DaIncassare d={data} viste={viste} canWrite={canWrite} canReadDocs={canReadDocs} storico={storico.on} />}
        {vista === 'documenti' && canReadDocs && <Emesse viste={viste} storico={storico.on} />}
        {vista === 'preventivi' && canReadDocs && <Preventivi viste={viste} storico={storico.on} />}
      </MPage>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La risposta
// ════════════════════════════════════════════════════════════════════

function Risposta({ d }: { d: DaIncassare }) {
  const t = d.totals;
  const totale = t.ritardo.amount + t.prossimi.amount + t.avanti.amount;
  const vecchia = d.items.find((r) => r.gruppo === 'RITARDO');
  const prossima = d.items.find((r) => r.gruppo === 'PROSSIMI') ?? d.items.find((r) => r.gruppo === 'AVANTI');
  const giorni = vecchia ? -(giorniDaOggi(vecchia.dueDate) ?? 0) : 0;
  return (
    <MHero
      label="Ci devono i clienti"
      unit={totale > 0 ? '€' : undefined}
      value={totale > 0 ? fmtEur(totale) : 'Niente'}
      sub={t.ritardo.fatture > 0 ? (
        <>
          <span className="is-alert">{plurale(t.ritardo.fatture, 'fattura in ritardo', 'fatture in ritardo')}</span>
          {giorni > 0 && <>, la più vecchia da {durata(giorni)}</>}
        </>
      ) : prossima ? `nessuna in ritardo · la prima scade ${quando(prossima.dueDate)}` : 'tutte le fatture emesse risultano incassate'}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// Da incassare
// ════════════════════════════════════════════════════════════════════

function DaIncassare({ d, viste, canWrite, canReadDocs, storico }: {
  d: DaIncassare | undefined; viste: ReactNode; canWrite: boolean; canReadDocs: boolean; storico: boolean;
}) {
  const [status, setStatus] = useUrlState<string>('status', 'aperte');
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [sel, setSel] = useState<RataDaIncassare | null>(null);
  const [az, setAz] = useState<Az | null>(null);
  const { markPaid, ignoreDocs } = useAdminActions();

  if (status === 'incassate') return <Incassate viste={viste} canWrite={canWrite} canReadDocs={canReadDocs} storico={storico} onBack={() => setStatus('aperte')} />;
  if (status === 'cestinate') return <Tolte viste={viste} canWrite={canWrite} storico={storico} onBack={() => setStatus('aperte')} />;
  if (!d) return <>{viste}<MGroup title="Da incassare"><MEmpty>Carico le fatture da incassare…</MEmpty></MGroup></>;

  const s = search.trim().toLowerCase();
  const items = d.items.filter((r) => !s || `${r.invoiceNumber ?? ''} ${r.clientName}`.toLowerCase().includes(s));
  const inBanca = (r: RataDaIncassare) => !!(r.bonifico || dubbioDi(r));
  const banca = items.filter(inBanca);
  const gruppi = [
    { key: 'banca', titolo: 'Col bonifico già in banca', righe: banca },
    { key: 'ritardo', titolo: 'In ritardo', righe: items.filter((r) => !inBanca(r) && r.gruppo === 'RITARDO') },
    { key: 'prossimi', titolo: 'Scadono entro 30 giorni', righe: items.filter((r) => !inBanca(r) && r.gruppo === 'PROSSIMI') },
    { key: 'avanti', titolo: 'Scadono più avanti', righe: items.filter((r) => !inBanca(r) && r.gruppo === 'AVANTI') },
  ].filter((g) => g.righe.length > 0);

  const riga = (r: RataDaIncassare) => {
    const rata = rataInParole(r.seq, r.ofTotal);
    const b = r.bonifico;
    const dubbio = dubbioDi(r);
    const g = giorniDaOggi(r.dueDate);
    // Prima il tempo (è quello che fa decidere), poi la fattura. Nel gruppo
    // "In ritardo" basta "da 7 mesi": il ritardo lo dice già il titolo.
    const tempo = r.gruppo === 'RITARDO' && g != null && g < 0
      ? <span className="is-alert">da {durata(-g)}</span>
      : quando(r.dueDate);
    return (
      <MRow
        key={r.id}
        text={iniziali(r.clientName)}
        round
        title={nomeRiga(r.clientName)}
        sub={b ? `in banca c'è il bonifico del ${giornoBreve(b.settledOn)}`
          : dubbio ? dubbio.testo
          : <>{tempo} · fattura {r.invoiceNumber ?? 's/n'}{rata ? `, ${rata}` : ''}</>}
        subWrap={!!dubbio && !b}
        trail={euroRiga(r.amount)}
        onClick={() => setSel(r)}
      />
    );
  };

  return (
    <>
      <div className="amk-viste">
        {viste}
        {d.items.length > 6 && <MSearch value={search} onChange={setSearch} placeholder="Cerca un cliente o un numero di fattura" />}
      </div>

      {gruppi.length === 0 ? (
        <MGroup title="Da incassare">
          <MEmpty>{s ? `Nessuna fattura da incassare con "${search.trim()}".` : 'Nessuna fattura da incassare: i clienti hanno pagato tutto.'}</MEmpty>
        </MGroup>
      ) : gruppi.map((g) => (
        <MGroup key={g.key} title={g.titolo} count={g.righe.length}
          note={g.key === 'banca' ? 'Il bonifico torna con la fattura: tocca la riga e conferma l\'incasso.' : undefined}>
          {g.righe.map(riga)}
        </MGroup>
      ))}

      <MGroup title="Altre fatture">
        <MRow icon="check" title="Già incassate"
          sub={`${euro(d.totals.incassatoAnno.amount)} incassati nel ${d.totals.incassatoAnno.year}`}
          onClick={() => setStatus('incassate')} />
        <MRow icon="trash" title="Tolte dall'elenco" sub="rifiutate, doppioni, emesse per errore" onClick={() => setStatus('cestinate')} />
      </MGroup>

      {sel && (
        <FoglioRata r={sel} canWrite={canWrite} canReadDocs={canReadDocs} onClose={() => setSel(null)}
          onAz={(a) => { setSel(null); setAz(a); }} />
      )}

      {az?.kind === 'incassa' && (
        <SettlePopover
          mode="incassa"
          amount={az.r.amount}
          subtitle={rataInParole(az.r.seq, az.r.ofTotal) ? `${rataInParole(az.r.seq, az.r.ofTotal)} della fattura ${az.r.invoiceNumber ?? ''}` : `fattura ${az.r.invoiceNumber ?? ''}`}
          target={{ kind: 'INVOICE_PAYMENT', id: az.r.id }}
          withNote
          pending={markPaid.isPending}
          onClose={() => setAz(null)}
          onConfirm={(v) => {
            const r = az.r;
            setAz(null);
            markPaid.mutate({ paymentId: r.id, amount: r.amount, label: etichettaRata(r), ...v });
          }}
        />
      )}
      {az?.kind === 'sollecita' && <SollecitaPopover rata={az.r} onClose={() => setAz(null)} />}
      {az?.kind === 'togli' && (
        <TrashPopover
          title={az.r.invoiceNumber ?? 's/n'}
          pending={ignoreDocs.isPending}
          onClose={() => setAz(null)}
          onConfirm={(v) => {
            const r = az.r;
            setAz(null);
            ignoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label: etichettaRata(r), ...v });
          }}
        />
      )}
    </>
  );
}

/** Il pannello di una rata: i fatti, il bonifico se c'è, e i gesti. */
function FoglioRata({ r, canWrite, canReadDocs, onClose, onAz }: {
  r: RataDaIncassare; canWrite: boolean; canReadDocs: boolean; onClose: () => void; onAz: (a: Az) => void;
}) {
  const navigate = useNavigate();
  const { confermaProposta } = useAdminActions();
  const rata = rataInParole(r.seq, r.ofTotal);
  const ritardo = r.gruppo === 'RITARDO';
  const b = r.bonifico;
  const dubbio = dubbioDi(r);
  const conferma = (txId: string, settledOn: string | null) => {
    onClose();
    confermaProposta.mutate({ lato: 'INCASSO', rataId: r.id, txId, paidDate: settledOn ?? isoDay(), label: etichettaRata(r), importo: r.amount, nota: NOTA_CONFERMA });
  };
  // Il gesto principale: il bonifico che torna si conferma, altrimenti si segna incassata.
  const tx = b ?? (dubbio && !dubbio.scegli ? dubbio : null);
  const primary = canWrite ? (tx ? (
    <button className="btn btn-primary m-block" disabled={confermaProposta.isPending} onClick={() => conferma(tx.txId, tx.settledOn)}>
      <Icon name="check" size={12} /> Conferma l'incasso
    </button>
  ) : (
    <button className="btn btn-primary m-block" onClick={() => onAz({ kind: 'incassa', r })}>
      <Icon name="check" size={12} /> Segna incassata
    </button>
  )) : undefined;

  return (
    <Foglio open onClose={onClose} title={leggibile(r.clientName)} primary={primary}>
      <div className="mk-list">
        <Fatto label="Fattura" value={<>{r.invoiceNumber ?? 'senza numero'}{r.issueDate && <span className="s">del {giornoBreve(r.issueDate)}</span>}</>} />
        {rata && <Fatto label="Rata" value={rata.replace('rata ', '')} />}
        <Fatto label="Da pagare entro" alert={ritardo} value={<>{giornoBreve(r.dueDate) || 'senza data'}<span className="s">{quando(r.dueDate)}</span></>} />
        <Fatto label="Da incassare" value={euroEsatto(r.amount)} />
        {r.ofTotal > 1 && <Fatto label="Totale della fattura" value={euroEsatto(r.invoiceTotal)} />}
      </div>

      {(b || dubbio) && (
        <MGroup title="In banca">
          {b ? (
            <Testo>C'è un bonifico che torna: <strong>{euroEsatto(b.amount)}</strong> da {leggibile(b.counterparty ?? b.label)}, arrivato il {giornoBreve(b.settledOn)}. Confermandolo la rata risulta incassata con quel bonifico.</Testo>
          ) : dubbio && (
            <Testo>{dubbio.testo}</Testo>
          )}
          {canWrite && dubbio && !b && dubbio.scegli && (
            <Gesto icon="link" label="Scegli il bonifico fra i movimenti" onClick={() => {
              onClose();
              navigate(`/admin/banca?vista=movimenti&q=${encodeURIComponent(dubbio.counterparty ?? r.clientName)}&side=CREDIT&unmatched=1`);
            }} />
          )}
        </MGroup>
      )}

      <div className="mk-list">
        {canWrite && tx && <Gesto icon="check" label="Segna incassata in un altro modo" onClick={() => onAz({ kind: 'incassa', r })} />}
        <Gesto icon="mail" label="Prepara il sollecito" sub="un testo da copiare: da qui non parte niente" onClick={() => onAz({ kind: 'sollecita', r })} />
        {canReadDocs && <Gesto icon="file" label="Apri la fattura" onClick={() => { onClose(); navigate(`/invoices/${r.invoiceId}`); }} />}
        {canWrite && <Gesto icon="trash" label="Togli dall'elenco" danger onClick={() => onAz({ kind: 'togli', r })} />}
      </div>
    </Foglio>
  );
}

// ════════════════════════════════════════════════════════════════════
// Già incassate e tolte dall'elenco (`?status=`)
// ════════════════════════════════════════════════════════════════════

function Incassate({ viste, canWrite, canReadDocs, storico, onBack }: {
  viste: ReactNode; canWrite: boolean; canReadDocs: boolean; storico: boolean; onBack: () => void;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [quante, setQuante] = useState(PAGINA);
  const [sel, setSel] = useState<IncassoRow | null>(null);
  const { unmarkPaid } = useAdminActions();
  const q = useQuery({
    ...incassiQuery({ status: 'incassate', search: search.trim() || undefined, includeHistory: storico, page: 1, pageSize: quante }),
    placeholderData: keepPreviousData,
  });
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;

  return (
    <>
      <div className="amk-viste">
        {viste}
        <MSearch value={search} onChange={setSearch} placeholder="Cerca un cliente o un numero di fattura" />
      </div>
      <MGroup title="Già incassate" count={q.data ? total : undefined} action={{ label: 'Da incassare', onClick: onBack }}>
        {q.isLoading && !q.data ? <MEmpty>Carico le fatture incassate…</MEmpty>
          : items.length === 0 ? <MEmpty>{search.trim() ? `Nessuna fattura incassata con "${search.trim()}".` : 'Nessuna rata incassata.'}</MEmpty>
          : (
            <>
              {items.map((r) => (
                <MRow
                  key={r.id}
                  text={iniziali(r.client.shortName)}
                  round
                  title={nomeRiga(r.client.shortName)}
                  sub={r.status === 'STORNATA'
                    ? `stornata su FattureInCloud · fattura ${r.invoiceNumber}`
                    : `il ${giornoBreve(r.paidDate)} · fattura ${r.invoiceNumber}`}
                  trail={euroRiga(r.amount)}
                  onClick={() => setSel(r)}
                />
              ))}
              {total > items.length && <MMore label={`Vedi altre ${Math.min(PAGINA, total - items.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />}
            </>
          )}
      </MGroup>

      {sel && (
        <Foglio open onClose={() => setSel(null)} title={leggibile(sel.client.shortName)}
          primary={canWrite && sel.status !== 'STORNATA' ? (
            <button className="btn m-block" disabled={unmarkPaid.isPending} onClick={() => { const r = sel; setSel(null); unmarkPaid.mutate({ paymentId: r.id, label: etichettaRata(r) }); }}>
              <Icon name="undo" size={12} /> Rimetti da incassare
            </button>
          ) : undefined}>
          <div className="mk-list">
            <Fatto label="Fattura" value={sel.invoiceNumber} />
            {rataInParole(sel.seq, sel.ofTotal) && <Fatto label="Rata" value={rataInParole(sel.seq, sel.ofTotal)!.replace('rata ', '')} />}
            <Fatto label="Incassata il" value={sel.status === 'STORNATA' ? 'stornata su FattureInCloud' : giornoBreve(sel.paidDate) || '—'} />
            <Fatto label="Pagata con" value={sel.method ? METHOD_LABEL[sel.method] ?? sel.method : '—'} />
            <Fatto label="Importo" value={euroEsatto(sel.amount)} />
          </div>
          {sel.localPaidAt && (
            <div className="mk-list">
              <Testo>Segnata incassata qui: su FattureInCloud risulta ancora aperta finché il commercialista non la registra.{sel.localPaidNote ? ` Nota: ${sel.localPaidNote}` : ''}</Testo>
            </div>
          )}
          {canReadDocs && (
            <div className="mk-list">
              <Gesto icon="file" label="Apri la fattura" onClick={() => { const id = sel.invoiceId; setSel(null); navigate(`/invoices/${id}`); }} />
            </div>
          )}
        </Foglio>
      )}
    </>
  );
}

function Tolte({ viste, canWrite, storico, onBack }: { viste: ReactNode; canWrite: boolean; storico: boolean; onBack: () => void }) {
  const [quante, setQuante] = useState(PAGINA);
  const [sel, setSel] = useState<IncassoRow | null>(null);
  const { restoreDocs } = useAdminActions();
  const q = useQuery({
    ...incassiQuery({ status: 'cestinate', includeHistory: storico, page: 1, pageSize: quante }),
    placeholderData: keepPreviousData,
  });
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  return (
    <>
      {viste}
      <MGroup title="Tolte dall'elenco" count={q.data ? total : undefined} action={{ label: 'Da incassare', onClick: onBack }}
        note="Non entrano nei totali, ma su FattureInCloud restano: per annullarne una davvero serve una nota di credito.">
        {q.isLoading && !q.data ? <MEmpty>Carico le fatture tolte…</MEmpty>
          : items.length === 0 ? <MEmpty>Nessuna fattura tolta dall'elenco.</MEmpty>
          : (
            <>
              {items.map((r) => (
                <MRow
                  key={r.id}
                  title={nomeRiga(r.client.shortName)}
                  sub={`${r.invoiceNumber} · ${r.ignoredReason ? IGNORE_REASON_LABEL[r.ignoredReason] ?? r.ignoredReason : 'tolta dall\'elenco'}`}
                  trail={<span className="amk-manca">{euroRiga(r.amount)}</span>}
                  onClick={() => setSel(r)}
                />
              ))}
              {total > items.length && <MMore label={`Vedi altre ${Math.min(PAGINA, total - items.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />}
            </>
          )}
      </MGroup>

      {sel && (
        <Foglio open onClose={() => setSel(null)} title={leggibile(sel.client.shortName)}
          primary={canWrite ? (
            <button className="btn m-block" disabled={restoreDocs.isPending} onClick={() => { const r = sel; setSel(null); restoreDocs.mutate({ kind: 'invoice', ids: [r.invoiceId], label: etichettaRata(r) }); }}>
              <Icon name="undo" size={12} /> Rimetti nell'elenco
            </button>
          ) : undefined}>
          <div className="mk-list">
            <Fatto label="Fattura" value={sel.invoiceNumber} />
            <Fatto label="Tolta il" value={giornoBreve(sel.ignoredAt) || '—'} />
            <Fatto label="Perché" value={sel.ignoredReason ? IGNORE_REASON_LABEL[sel.ignoredReason] ?? sel.ignoredReason : '—'} />
            <Fatto label="Importo" value={euroEsatto(sel.amount)} />
          </div>
          {sel.ignoredNote && <div className="mk-list"><Testo>{sel.ignoredNote}</Testo></div>}
        </Foglio>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Emesse da noi
// ════════════════════════════════════════════════════════════════════

const ORDINI_FATTURE: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Emesse più di recente prima' },
  { key: 'vecchie', label: 'Emesse da più tempo prima' },
  { key: 'scadenza', label: 'Scadenza del pagamento più vicina prima' },
  { key: 'importo', label: 'Prima gli importi più alti' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];

/** Le stesse chiavi del computer (`type`, `status`): un link filtrato funziona su tutti e due. */
const FILTRI_EMESSE: FiltroDef[] = [
  {
    param: 'type', label: 'Tipo di documento',
    opzioni: [
      { value: 'FATTURA', label: 'Solo le fatture' },
      { value: 'NOTA_CREDITO', label: 'Note di credito' },
      { value: 'PROFORMA', label: 'Pre-fatture' },
      { value: 'AVVISO_PAGAMENTO', label: 'Avvisi di pagamento' },
    ],
  },
  {
    param: 'status', label: 'Stato del pagamento',
    opzioni: [
      { value: InvoiceStatus.EMESSA, label: 'Da incassare dai clienti' },
      { value: InvoiceStatus.SCADUTA, label: 'In ritardo col pagamento' },
      { value: InvoiceStatus.PAGATA, label: 'Pagate dal cliente' },
      { value: InvoiceStatus.BOZZA, label: 'Bozze non emesse' },
      { value: InvoiceStatus.INVIATA, label: 'Inviate al cliente, da incassare' },
      { value: InvoiceStatus.ANNULLATA, label: 'Annullate' },
    ],
  },
];

const TIPO_DOC: Record<string, string> = { NOTA_CREDITO: 'Nota di credito', PROFORMA: 'Pre-fattura', AVVISO_PAGAMENTO: 'Avviso di pagamento' };

/** Lo stato di una fattura detto nella riga: rosso solo se è in ritardo. */
function statoFattura(r: UnifiedRow): ReactNode {
  const g = daysFromToday(r.secondaryDate);
  const aperta = ['EMESSA', 'INVIATA', 'SCADUTA'].includes(r.status);
  if (aperta && g != null && g < 0) return <span className="is-alert">in ritardo da {durata(-g)}</span>;
  if (aperta) return r.secondaryDate ? `da incassare entro il ${giornoBreve(r.secondaryDate)}` : 'da incassare';
  if (r.status === 'PAGATA') return 'pagata';
  if (r.status === 'BOZZA') return 'bozza, non ancora emessa';
  if (r.status === 'ANNULLATA') return 'annullata';
  return (STATUS_INFO[r.status]?.label ?? r.status).toLowerCase();
}

function Emesse({ viste, storico }: { viste: ReactNode; storico: boolean }) {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const ordina = useOrdina(ORDINI_FATTURE);
  const filtri = useFiltri(FILTRI_EMESSE);
  const [quante, setQuante] = useState(PAGINA);
  const tipo = filtri.valore('type');
  const source: DocSource = ['FATTURA', 'NOTA_CREDITO', 'PROFORMA', 'AVVISO_PAGAMENTO'].includes(tipo) ? (tipo as DocSource) : 'all';
  const q = useQuery({
    ...documentsQuery({ source, status: filtri.valore('status'), search: search.trim(), sort: ordina.key, includeHistory: storico, page: 1, pageSize: quante }),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const perMese = ordina.key === 'recenti' || ordina.key === 'vecchie';
  const gruppi = useMemo(
    () => (perMese ? aGruppi(rows, (r) => (r.primaryDate ?? '').slice(0, 7)) : [{ chiave: 'tutte', items: rows }]),
    [rows, perMese],
  );

  const riga = (r: UnifiedRow) => (
    <MRow
      key={r.id}
      title={nomeRiga(r.client.legalName)}
      sub={<>{r.invoiceType && TIPO_DOC[r.invoiceType] ? `${TIPO_DOC[r.invoiceType]} ` : ''}{r.number ?? 'senza numero'} · {statoFattura(r)}</>}
      trail={euroRiga(Number(r.total))}
      onClick={() => navigate(`/invoices/${r.id}`)}
    />
  );

  return (
    <>
      <div className="amk-viste">
        {viste}
        <div className="amk-tools">
          <MSearch value={search} onChange={setSearch} placeholder="Numero o nota" />
          <OrdinaFiltra ordina={ordina} filtri={filtri} label="Filtra" />
        </div>
      </div>

      {q.isLoading && !q.data ? (
        <MGroup title="Fatture emesse"><MEmpty>Carico le fatture…</MEmpty></MGroup>
      ) : rows.length === 0 ? (
        <MGroup title="Fatture emesse">
          <MEmpty>{search.trim() || filtri.attivi ? 'Nessuna fattura con questa ricerca o questi filtri.' : 'Nessuna fattura emessa.'}</MEmpty>
        </MGroup>
      ) : gruppi.map((g, i) => (
        <MGroup
          key={g.chiave}
          title={perMese ? meseGruppo(g.chiave) : ordina.attiva.label}
          count={g.items.length}
          note={i === gruppi.length - 1 ? 'Le fatture nuove si creano dal computer.' : undefined}
        >
          {g.items.map(riga)}
          {i === gruppi.length - 1 && total > rows.length && (
            <MMore label={`Vedi altre ${Math.min(PAGINA, total - rows.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />
          )}
        </MGroup>
      ))}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Preventivi
// ════════════════════════════════════════════════════════════════════

const ORDINI_PREVENTIVI: OpzioneOrdina[] = [
  { key: 'recenti', label: 'Emessi più di recente prima' },
  { key: 'vecchie', label: 'Emessi da più tempo prima' },
  { key: 'validita', label: 'Scadenza della proposta più vicina prima' },
  { key: 'importo', label: 'Prima gli importi più alti' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];

const FILTRI_PREVENTIVI: FiltroDef[] = [
  {
    param: 'status', label: 'Stato della firma',
    opzioni: [
      { value: QuoteStatus.INVIATO, label: 'Inviati al cliente' },
      { value: QuoteStatus.VISUALIZZATO, label: 'Aperti dal cliente' },
      { value: QuoteStatus.ACCETTATO, label: 'Firmati dal cliente' },
      { value: QuoteStatus.BOZZA, label: 'Bozze non inviate' },
      { value: QuoteStatus.CONVERTITO, label: 'Diventati fattura' },
      { value: QuoteStatus.RIFIUTATO, label: 'Rifiutati dal cliente' },
      { value: QuoteStatus.SCADUTO, label: 'Scaduti senza firma' },
    ],
  },
];

/** I preventivi per quello che c'è da fare: prima i firmati da fatturare, poi quelli da firmare. */
const STATI_PREVENTIVO: Array<{ titolo: string; stati: string[] }> = [
  { titolo: 'Firmati, da fatturare', stati: ['ACCETTATO'] },
  { titolo: 'In attesa della firma', stati: ['INVIATO', 'VISUALIZZATO'] },
  { titolo: 'Bozze', stati: ['BOZZA'] },
  { titolo: 'Chiusi', stati: ['CONVERTITO', 'RIFIUTATO', 'SCADUTO'] },
];

function Preventivi({ viste, storico }: { viste: ReactNode; storico: boolean }) {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const ordina = useOrdina(ORDINI_PREVENTIVI);
  const filtri = useFiltri(FILTRI_PREVENTIVI);
  const [quante, setQuante] = useState(PAGINA * 2);
  const q = useQuery({
    ...documentsQuery({ source: 'quote', status: filtri.valore('status'), search: search.trim(), sort: ordina.key, includeHistory: storico, page: 1, pageSize: quante }),
    placeholderData: keepPreviousData,
  });
  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;
  const gruppi = STATI_PREVENTIVO.map((g) => ({ ...g, righe: rows.filter((r) => g.stati.includes(r.status)) })).filter((g) => g.righe.length > 0);
  const cercando = !!search.trim() || filtri.attivi > 0;

  return (
    <>
      <div className="amk-viste">
        {viste}
        {/* Senza preventivi la ricerca non ha niente da cercare. */}
        {(total > 0 || cercando) && (
          <div className="amk-tools">
            <MSearch value={search} onChange={setSearch} placeholder="Numero o titolo" />
            <OrdinaFiltra ordina={ordina} filtri={filtri} label="Filtra" />
          </div>
        )}
      </div>

      {q.isLoading && !q.data ? (
        <MGroup title="Preventivi"><MEmpty>Carico i preventivi…</MEmpty></MGroup>
      ) : gruppi.length === 0 ? (
        <MGroup title="Preventivi" note="I preventivi nuovi si creano dal computer.">
          <MEmpty>{cercando ? 'Nessun preventivo con questa ricerca o questi filtri.' : 'Nessun preventivo, per ora.'}</MEmpty>
        </MGroup>
      ) : gruppi.map((g, i) => (
        <MGroup key={g.titolo} title={g.titolo} count={g.righe.length}
          note={i === gruppi.length - 1 ? 'I preventivi nuovi si creano dal computer.' : undefined}>
          {g.righe.map((r) => (
            <MRow
              key={r.id}
              title={nomeRiga(r.client.legalName)}
              // Il titolo della proposta dice cos'è più del numero ("11" da solo non dice niente).
              sub={`${r.subtitle} · ${(STATUS_INFO[r.status]?.label ?? r.status).toLowerCase()}`}
              trail={euroRiga(Number(r.total))}
              onClick={() => navigate(`/quotes/${r.id}`)}
            />
          ))}
          {i === gruppi.length - 1 && total > rows.length && (
            <MMore label={`Vedi altri ${Math.min(PAGINA, total - rows.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />
          )}
        </MGroup>
      ))}
    </>
  );
}
